import { DateFormatPresetId, defaultDateFormatPresetId } from '@/helpers/dates';
import { Org } from '@/types';
import { SIDEBAR_DEFAULT_SIZE } from '@components/Sidebar/constants';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

type State = {
  isOrgSelectorOpen: boolean;
  isSidebarCollapsed: boolean;
  isSidebarMenuOpen: boolean;
  selectedOrganization: Org | undefined;
  dateFormatPresetId: DateFormatPresetId;
  sidebarSize: number;
};

type Actions = {
  setIsOrgSelectorOpen: (isOpen: boolean) => void;
  setIsSidebarCollapsed: (isCollapsed: boolean) => void;
  setIsSidebarMenuOpen: (isOpen: boolean) => void;
  setSelectedOrganization: (org: Org | undefined) => void;
  setDateFormatPresetId: (dateFormatPresetId: DateFormatPresetId) => void;
  setSidebarSize: (size: number) => void;
};

const useAppStore = create<State & Actions>()(
  devtools(
    persist(
      immer(set => ({
        isOrgSelectorOpen: false,
        isSidebarCollapsed: false,
        isSidebarMenuOpen: false,
        selectedOrganization: undefined,
        dateFormatPresetId: defaultDateFormatPresetId,
        sidebarSize: SIDEBAR_DEFAULT_SIZE,
        setIsOrgSelectorOpen: (isOpen: boolean) =>
          set(state => {
            state.isOrgSelectorOpen = isOpen;
          }),
        setIsSidebarCollapsed: (isCollapsed: boolean) =>
          set(state => {
            state.isSidebarCollapsed = isCollapsed;
          }),
        setIsSidebarMenuOpen: (isOpen: boolean) =>
          set(state => {
            state.isSidebarMenuOpen = isOpen;
          }),
        setSelectedOrganization: (org: Org | undefined) =>
          set(state => {
            state.selectedOrganization = org;
          }),
        setDateFormatPresetId: (dateFormatPresetId: DateFormatPresetId) =>
          set(state => {
            state.dateFormatPresetId = dateFormatPresetId;
          }),
        setSidebarSize: (size: number) =>
          set(state => {
            state.sidebarSize = size;
          }),
      })),
      {
        name: 'app',
        partialize: state =>
          Object.fromEntries(
            Object.entries(state).filter(([key]) =>
              [
                'isSidebarCollapsed',
                'isSidebarMenuOpen',
                'selectedOrganization',
                'dateFormatPresetId',
                'sidebarSize',
              ].includes(key),
            ),
          ),
      },
    ),
  ),
);

export { useAppStore };
